import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { signIn, signUp, completeLogin } from '../lib/auth';
import { getStudyAssociations, type StudyAssociation } from '../lib/admin';
import { isDeviceTrusted, sendDeviceVerification, verifyDevice } from '../lib/deviceAuth';

interface AuthFormProps {
  mode: 'login' | 'register';
}

interface Member {
  id: string;
  organization_name: string;
  study_association_id: string;
}

interface InvitationData {
  email: string;
  role: string;
  study_association_id: string;
  study_association_name: string;
  member_id: string | null;
  member_name: string | null;
}

export default function AuthForm({ mode }: AuthFormProps) {
  const [searchParams] = useSearchParams();
  const urlInvitationToken = searchParams.get('invitation');

  // Token kan komme fra URL ELLER fra manuell input (fallback hvis e-postklient
  // trunkerte lenken). Manual-input state vises kun ved /register uten URL-token,
  // eller ved invitationError fra URL-token.
  const [manualToken, setManualToken] = useState('');
  const invitationToken = urlInvitationToken || (manualToken || null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [studyAssociationId, setStudyAssociationId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [studyAssociations, setStudyAssociations] = useState<StudyAssociation[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [needsDeviceVerification, setNeedsDeviceVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);

  // SECURITY: Store pending session until device verification completes
  const [pendingSession, setPendingSession] = useState<{ token: string; user: any } | null>(null);

  // Invitation state
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(false);
  const [invitationError, setInvitationError] = useState('');

  // Verify invitation token if present
  useEffect(() => {
    if (mode === 'register' && invitationToken) {
      setLoadingInvitation(true);
      setInvitationError('');

      fetch(`/api/auth/verify-invitation/${invitationToken}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.invitation) {
            setInvitation(data.invitation);
            setEmail(data.invitation.email);
            setStudyAssociationId(data.invitation.study_association_id);
            if (data.invitation.member_id) {
              setMemberId(data.invitation.member_id);
            }
          } else {
            setInvitationError(data.error || 'Ugyldig eller utløpt invitasjon');
          }
        })
        .catch((err) => {
          console.error('Failed to verify invitation:', err);
          setInvitationError('Kunne ikke verifisere invitasjon');
        })
        .finally(() => {
          setLoadingInvitation(false);
        });
    }
  }, [mode, invitationToken]);

  // Load study associations for registration (only if not invited)
  useEffect(() => {
    if (mode === 'register' && !invitationToken) {
      setLoadingAssociations(true);
      getStudyAssociations()
        .then((associations) => {
          setStudyAssociations(associations);
          // Auto-select first association if only one exists
          if (associations.length === 1) {
            setStudyAssociationId(associations[0].id);
          }
        })
        .catch((err) => {
          console.error('Failed to load study associations:', err);
          setError('Kunne ikke laste studieforbund');
        })
        .finally(() => {
          setLoadingAssociations(false);
        });
    }
  }, [mode, invitationToken]);

  // Load members when study association is selected (only if not invited)
  useEffect(() => {
    if (mode === 'register' && studyAssociationId && !invitation) {
      setLoadingMembers(true);
      setMemberId(''); // Reset member selection
      fetch(`/public/members/${studyAssociationId}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setMembers(data.members);
            // Auto-select if only one member
            if (data.members.length === 1) {
              setMemberId(data.members[0].id);
            }
          } else {
            setError('Kunne ikke laste medlemsorganisasjoner');
          }
        })
        .catch((err) => {
          console.error('Failed to load members:', err);
          setError('Kunne ikke laste medlemsorganisasjoner');
        })
        .finally(() => {
          setLoadingMembers(false);
        });
    }
  }, [mode, studyAssociationId, invitation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        // Sign in but DON'T save session yet (security fix)
        const result = await signIn({ email, password }, false);

        // Check if device is trusted (if user wants device to be trusted)
        if (trustDevice) {
          // Pass user ID from login result since session is not saved yet
          const trusted = await isDeviceTrusted(result.user.id);
          if (!trusted) {
            // SECURITY: Store session in memory, not localStorage
            // Will only be saved after device verification completes
            setPendingSession({
              token: result.session.access_token,
              user: result.user
            });
            // Send verification code to user's email
            await sendDeviceVerification('Current Device', result.user.id);
            setNeedsDeviceVerification(true);
            setLoading(false);
            return;
          }
        }

        // Device is trusted or user chose not to trust device
        // NOW save the session and proceed to dashboard
        completeLogin(result.session.access_token, result.user);
        window.location.href = '/';
      } else {
        // Handle invitation-based registration
        if (invitation && invitationToken) {
          // Register via invitation
          const response = await fetch('/api/auth/register-invited', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              token: invitationToken,
              password,
              full_name: fullName,
            }),
          });

          const result = await response.json();

          if (!result.success) {
            setError(result.error || 'Registrering feilet');
            setLoading(false);
            return;
          }

          // Show success message - invited users can log in immediately
          setRegistrationSuccess(true);
        } else {
          // Normal registration flow
          // Validate study association is selected
          if (!studyAssociationId) {
            setError('Vennligst velg studieforbund');
            setLoading(false);
            return;
          }

          // Validate member organization is selected
          if (!memberId) {
            setError('Vennligst velg medlemsorganisasjon');
            setLoading(false);
            return;
          }

          // Register user
          await signUp({
            email,
            password,
            full_name: fullName,
            study_association_id: studyAssociationId,
            member_id: memberId,
          });
          // Show success message (don't auto-login, user must verify email first)
          setRegistrationSuccess(true);
        }
      }
    } catch (err: any) {
      setError(err.message || 'En feil oppstod');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Pass user ID from pendingSession since session is not saved yet
      const userId = pendingSession?.user?.id;
      const verified = await verifyDevice(verificationCode, 'Current Device', userId);
      if (verified) {
        // SECURITY: Only NOW save the session after device verification
        if (pendingSession) {
          completeLogin(pendingSession.token, pendingSession.user);
          setPendingSession(null);
        }
        window.location.href = '/';
      } else {
        setError('Ugyldig eller utløpt verifiseringskode');
      }
    } catch (err: any) {
      setError(err.message || 'Verifisering feilet');
    } finally {
      setLoading(false);
    }
  };

  // Show success message after registration
  if (mode === 'register' && registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Registrering vellykket!
            </h2>
          </div>
          {invitation ? (
            // Invited user - can log in immediately
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800">
                Velkommen! Din brukerkonto er nå opprettet.
              </p>
              <p className="text-sm text-green-800 mt-2">
                Du kan nå logge inn med e-postadressen <strong>{email}</strong> og passordet du valgte.
              </p>
            </div>
          ) : (
            // Regular user - needs email verification
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800">
                En e-post med verifiseringslenke har blitt sendt til <strong>{email}</strong>.
              </p>
              <p className="text-sm text-green-800 mt-2">
                Vennligst sjekk innboksen din og klikk på lenken for å verifisere kontoen din.
              </p>
              <p className="text-sm text-green-800 mt-2">
                Du kan ikke logge inn før e-postadressen er verifisert.
              </p>
            </div>
          )}
          <div className="text-center space-y-2">
            <a
              href="/login"
              className="text-sm text-kf-primary hover:text-kf-secondary"
            >
              {invitation ? 'Gå til innlogging' : 'Tilbake til innlogging'}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Show device verification screen
  if (needsDeviceVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Verifiser enhet
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Vi har sendt en verifiseringskode til <strong>{email}</strong>
            </p>
            <p className="mt-1 text-center text-xs text-gray-500">
              Koden utløper om 15 minutter
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleVerifyDevice}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <div>
              <label htmlFor="code" className="label">
                Verifiseringskode
              </label>
              <input
                id="code"
                type="text"
                required
                className="input text-center text-2xl tracking-widest"
                placeholder="XXXXXX"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                maxLength={6}
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-gray-500">
                Finner du ikke e-posten? Sjekk søppelpost-mappen.
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? 'Verifiserer...' : 'Verifiser'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {mode === 'login' ? 'Logg inn' : 'Registrer deg'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Kompetanseattest Kvalitetssikring
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Manuell token-input — vises ved /register uten URL-token,
              eller ved invitationError (e-postklient kan ha trunkert lenken). */}
          {mode === 'register' && (!urlInvitationToken || invitationError) && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
              <p className="text-sm text-amber-900 font-medium mb-2">
                Har du fått en invitasjonskode på e-post?
              </p>
              <p className="text-xs text-amber-800 mb-3">
                Hvis lenken i e-posten ikke virker (noen e-postklienter klipper lange lenker),
                kan du kopiere selve invitasjonskoden og lime den inn her.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Lim inn invitasjonskoden"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value.trim())}
                  className="flex-1 rounded-md border border-amber-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (manualToken) {
                      // Trigger ny verifisering via state-oppdatering
                      setInvitationError('');
                    }
                  }}
                  disabled={!manualToken || manualToken.length < 8}
                  className="rounded-md bg-amber-600 text-white px-4 py-2 text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                >
                  Bruk kode
                </button>
              </div>
            </div>
          )}

          {/* Invitation banner */}
          {mode === 'register' && invitationToken && (
            loadingInvitation ? (
              <div className="rounded-md bg-blue-50 p-4">
                <p className="text-sm text-blue-800">Verifiserer invitasjon...</p>
              </div>
            ) : invitationError ? (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800 font-medium">Invitasjonsfeil</p>
                <p className="text-sm text-red-700 mt-1">{invitationError}</p>
                <a href="/register" className="text-sm text-red-600 underline mt-2 block">
                  Registrer deg uten invitasjon
                </a>
              </div>
            ) : invitation && (
              <div className="rounded-md bg-green-50 border border-green-200 p-4">
                <p className="text-sm text-green-800 font-medium">Du har blitt invitert!</p>
                <div className="mt-2 text-sm text-green-700 space-y-1">
                  <p><strong>Studieforbund:</strong> {invitation.study_association_name}</p>
                  {invitation.member_name && (
                    <p><strong>Organisasjon:</strong> {invitation.member_name}</p>
                  )}
                  <p><strong>Rolle:</strong> {invitation.role === 'admin' ? 'Administrator' : invitation.role === 'sensor' ? 'Sensor' : 'Medlem'}</p>
                </div>
              </div>
            )
          )}

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          <div className="space-y-4">
            {mode === 'register' && (
              <>
                <div>
                  <label htmlFor="fullName" className="label">
                    Fullt navn
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    required
                    className="input"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={loadingInvitation || !!invitationError}
                  />
                </div>

                {/* Show study association/member fields only for non-invited users */}
                {!invitation && (
                  <>
                    <div>
                      <label htmlFor="studyAssociation" className="label">
                        Studieforbund
                      </label>
                      {loadingAssociations ? (
                        <div className="input flex items-center justify-center">
                          <span className="text-sm text-gray-500">Laster...</span>
                        </div>
                      ) : (
                        <select
                          id="studyAssociation"
                          required
                          className="input"
                          value={studyAssociationId}
                          onChange={(e) => setStudyAssociationId(e.target.value)}
                        >
                          <option value="">Velg studieforbund</option>
                          {studyAssociations.map((association) => (
                            <option key={association.id} value={association.id}>
                              {association.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div>
                      <label htmlFor="memberOrganization" className="label">
                        Medlemsorganisasjon
                      </label>
                      {!studyAssociationId ? (
                        <div className="input flex items-center justify-center bg-gray-50">
                          <span className="text-sm text-gray-400">Velg studieforbund først</span>
                        </div>
                      ) : loadingMembers ? (
                        <div className="input flex items-center justify-center">
                          <span className="text-sm text-gray-500">Laster...</span>
                        </div>
                      ) : (
                        <select
                          id="memberOrganization"
                          required
                          className="input"
                          value={memberId}
                          onChange={(e) => setMemberId(e.target.value)}
                          disabled={!studyAssociationId}
                        >
                          <option value="">Velg medlemsorganisasjon</option>
                          {members.map((member) => (
                            <option key={member.id} value={member.id}>
                              {member.organization_name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            <div>
              <label htmlFor="email" className="label">
                E-postadresse
              </label>
              <input
                id="email"
                type="email"
                required
                className={`input ${invitation ? 'bg-gray-100' : ''}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                readOnly={!!invitation}
                disabled={loadingInvitation || !!invitationError}
              />
              {invitation && (
                <p className="mt-1 text-xs text-gray-500">E-postadressen er satt fra invitasjonen</p>
              )}
            </div>
            <div>
              <label htmlFor="password" className="label">
                Passord
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loadingInvitation || !!invitationError}
              />
            </div>
          </div>

          {mode === 'login' && (
            <div className="flex items-center">
              <input
                id="trustDevice"
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="h-4 w-4 text-kf-primary focus:ring-kf-primary border-gray-300 rounded"
              />
              <label htmlFor="trustDevice" className="ml-2 block text-sm text-gray-700">
                Husk meg på denne enheten (30 dager)
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || loadingInvitation || !!invitationError}
            className="btn btn-primary w-full"
          >
            {loading ? 'Vent...' : mode === 'login' ? 'Logg inn' : invitation ? 'Opprett konto' : 'Registrer'}
          </button>

          <div className="space-y-2">
            {mode === 'login' && (
              <div className="text-center">
                <a
                  href="/forgot-password"
                  className="text-sm text-gray-600 hover:text-kf-primary"
                >
                  Glemt passord?
                </a>
              </div>
            )}
            <div className="text-center">
              <a
                href={mode === 'login' ? '/register' : '/login'}
                className="text-sm text-kf-primary hover:text-kf-secondary"
              >
                {mode === 'login'
                  ? 'Har du ikke konto? Registrer deg'
                  : 'Har du allerede konto? Logg inn'}
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
